import { Icon } from '@iconify-icon/react';
import type { GetServerSideProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import PageLink from '@/utils/PageLink';

type CaseStudy = {
  slug: string;
  company: string;
  industry: string;
  headline: string;
  heroImage: string;
  intro: string;
  challenge: string[];
  solution: string[];
  solutionPoints: string[];
  results: { title: string; body: string }[];
  quote: { text: string; attribution: string };
  seoDescription: string;
  related?: { blurb: string; label: string; href: string };
};

const caseStudies: Record<string, CaseStudy> = {
  'maxeta-energy': {
    slug: 'maxeta-energy',
    company: 'MAXeta Energy',
    related: {
      blurb: 'Explore cited research workflows for energy and regulatory teams.',
      label: 'Chat.co for Energy',
      href: '/solutions/energy',
    },
    industry: 'Energy Consulting & Regulatory Strategy',
    headline: 'How MAXeta Energy cut case-specific research from hours to minutes',
    heroImage: '/images/landing/maxeta-energy.png',
    intro:
      'MAXeta Energy advises on complex energy regulatory proceedings where a single rate case can generate tens of thousands of pages of filings, orders, and testimony. With Chat.co Energy Expert Agents, their analysts now get cited answers in minutes.',
    challenge: [
      'Regulatory work runs on precedent. Before answering a client question or drafting a position, MAXeta analysts had to locate the relevant language across years of docket filings, commission orders, technical testimony, and hearing transcripts — spread across thousands of documents.',
      'That research was measured in hours per question. And because the stakes are regulatory, every answer had to be traceable back to the exact source document — a requirement generic AI tools could not meet.',
    ],
    solution: [
      'MAXeta deployed Energy Expert Agents on Chat.co: dedicated AI agents, each trained on the complete document record of a specific docket or regulatory domain.',
      'Analysts ask questions in plain language and get back answers grounded in the actual filings — with citations to the specific source documents, so every claim can be verified and carried straight into work product.',
    ],
    solutionPoints: [
      'Dedicated expert agent per docket / regulatory domain',
      'Trained on filings, orders, testimony, and transcripts',
      'Every answer cited to the underlying source document',
      'Secure, access-controlled deployment for the consulting team',
    ],
    results: [
      {
        title: 'Hours to minutes',
        body: 'Case-specific research that used to take hours of manual document review now takes minutes.',
      },
      {
        title: 'Citations built in',
        body: 'Answers arrive with source citations analysts can verify and use directly in regulatory work product.',
      },
      {
        title: 'Institutional knowledge, preserved',
        body: 'Docket expertise no longer lives only in senior analysts’ heads — it is queryable by the whole team.',
      },
    ],
    quote: {
      text: 'Case-specific research that used to take our analysts hours now takes minutes — and every answer comes with the record citation to back it up.',
      attribution: 'MAXeta Energy',
    },
    seoDescription:
      'How MAXeta Energy uses Chat.co Energy Expert Agents to cut case-specific regulatory research from hours to minutes, with every answer cited to source filings.',
  },
  'calvario-academy': {
    slug: 'calvario-academy',
    company: 'Calvario City Christian Academy & Pre-School',
    industry: 'Private K-12 & Early Childhood Education',
    headline: 'How Calvario City replaced thousands of printed pages with a Faculty Portal',
    heroImage: '/images/landing/calvario-academy.png',
    intro:
      'Calvario City Christian Academy & Pre-School ran its faculty operations on printed teacher manuals — thousands of pages reprinted as policies changed. With a Chat.co-powered Faculty Portal, staff now get instant, cited answers from the current version of every document.',
    challenge: [
      'Teacher manuals, school policies, curriculum guides, and procedures lived in binders. Every update meant reprinting, redistributing, and hoping the old copies left circulation — thousands of printed pages a year.',
      'When a teacher needed an answer — a grading policy, an emergency procedure, a pre-school protocol — finding it meant paging through a manual, and there was no guarantee the copy in hand was current.',
    ],
    solution: [
      'The school launched a Faculty Portal built on Chat.co: an access-restricted AI agent trained on the complete, current set of manuals, policies, and procedures.',
      'Faculty sign in and ask questions in plain language. The agent answers instantly and cites the exact manual section, so staff can trust the answer — and always get the current version, never a stale printout.',
    ],
    solutionPoints: [
      'Authenticated Faculty Portal — staff-only access',
      'Trained on teacher manuals, policies & procedures',
      'Answers cite the exact section of the source manual',
      'One update to a document updates every answer',
    ],
    results: [
      {
        title: 'Printing eliminated',
        body: 'From thousands of printed manual pages per year to zero — updates ship instantly to every teacher.',
      },
      {
        title: 'Instant, cited answers',
        body: 'Faculty get answers in seconds, cited to the manual section, instead of paging through binders.',
      },
      {
        title: 'Always current',
        body: 'Policy changes take effect the moment a document is updated — no stale copies in circulation.',
      },
    ],
    quote: {
      text: 'We used to print thousands of pages of teacher manuals every year. Now our faculty gets instant, cited answers through our own Faculty Portal.',
      attribution: 'Calvario City Christian Academy & Pre-School',
    },
    seoDescription:
      'How Calvario City Christian Academy & Pre-School replaced thousands of printed manual pages with a Chat.co-powered Faculty Portal delivering instant, cited answers.',
    related: {
      blurb: 'We build custom Faculty Portals like this one for every school.',
      label: 'Explore Chat.co for Schools',
      href: '/schools',
    },
  },
};

interface CaseStudyPageProps {
  slug: string;
}

const CaseStudyPage: React.FC<CaseStudyPageProps> = ({ slug }) => {
  const study = caseStudies[slug];

  return (
    <div className="bg-white">
      <NextSeo
        title={`${study.company} Case Study`}
        description={study.seoDescription}
        canonical={`https://chat.co/case-studies/${study.slug}`}
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#0047900a_1px,transparent_1px),linear-gradient(to_bottom,#0047900a_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" />
        </div>
        <div className="relative mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <span className="inline-flex items-center rounded-full bg-gray-100 px-4 py-2 text-sm font-medium uppercase text-gray-900">
            Case Study · {study.industry}
          </span>
          <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
            {study.headline}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-gray-600">{study.intro}</p>
          <div className="relative mt-10 aspect-[1.91/1] w-full overflow-hidden rounded-[28px] shadow-lg">
            <Image
              src={study.heroImage}
              alt={`${study.company} and Chat.co illustration`}
              fill
              className="object-cover"
              priority
            />
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="bg-white pb-4">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <figure className="rounded-[28px] bg-[#edf2f8] p-8 sm:p-10">
            <Icon icon="tabler:quote" className="text-4xl text-primary-600/40" />
            <blockquote className="mt-4 text-xl font-medium leading-relaxed text-gray-900 sm:text-2xl">
              &ldquo;{study.quote.text}&rdquo;
            </blockquote>
            <figcaption className="mt-5 text-sm font-semibold text-gray-500">
              — {study.quote.attribution}
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Challenge / Solution / Results */}
      <section className="bg-white py-12 sm:py-16">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">The challenge</h2>
          {study.challenge.map((p) => (
            <p key={p.slice(0, 32)} className="mt-4 text-base leading-relaxed text-gray-600">
              {p}
            </p>
          ))}

          <h2 className="mt-12 text-2xl font-bold text-gray-900 sm:text-3xl">The solution</h2>
          {study.solution.map((p) => (
            <p key={p.slice(0, 32)} className="mt-4 text-base leading-relaxed text-gray-600">
              {p}
            </p>
          ))}
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {study.solutionPoints.map((point) => (
              <li key={point} className="flex items-start gap-2.5 rounded-2xl bg-[#f8fafc] p-4">
                <Icon
                  icon="tabler:circle-check-filled"
                  className="mt-0.5 text-lg text-primary-600"
                />
                <span className="text-sm font-medium text-gray-900">{point}</span>
              </li>
            ))}
          </ul>

          <h2 className="mt-12 text-2xl font-bold text-gray-900 sm:text-3xl">The results</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {study.results.map((r) => (
              <div key={r.title} className="rounded-[28px] border border-gray-100 bg-white p-6">
                <div className="text-lg font-semibold text-gray-900">{r.title}</div>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related page banner */}
      {study.related && (
        <section className="bg-white pb-12">
          <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
            <Link
              href={study.related.href}
              className="group flex flex-col items-start justify-between gap-3 rounded-[28px] border border-primary-600/15 bg-[#f6f8fb] px-8 py-6 transition-colors duration-200 hover:border-primary-600/40 sm:flex-row sm:items-center">
              <span className="text-base font-medium text-gray-800">{study.related.blurb}</span>
              <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary-600">
                {study.related.label}
                <Icon
                  icon="tabler:arrow-right"
                  className="text-base transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </span>
            </Link>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-white pb-16 sm:pb-20">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-[28px] bg-primary-600 px-8 py-10 text-center sm:px-12">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">
              Ready to put your knowledge to work?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-blue-100">
              Try your first agent for 14 days, or talk to us about your use case.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href={PageLink.login}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-primary-600 hover:bg-blue-50 sm:w-auto">
                Start 14-Day Trial
                <Icon icon="tabler:arrow-right" className="text-lg" />
              </Link>
              <Link
                href={PageLink.demo}
                className="inline-flex w-full items-center justify-center rounded-full border border-white/40 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 sm:w-auto">
                Book a demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<CaseStudyPageProps> = async ({ params }) => {
  const slug = typeof params?.slug === 'string' ? params.slug : '';
  if (!caseStudies[slug]) {
    return { notFound: true };
  }
  return { props: { slug } };
};

export default CaseStudyPage;
